import Swiper from "swiper";
import { Pagination, Navigation, Thumbs, FreeMode, A11y } from "swiper/modules";
import lightGallery from "lightgallery";
import lgVideo from "lightgallery/plugins/video";
import * as HeadroomModule from "headroom.js";
import { clearAllBodyScrollLocks } from "body-scroll-lock";
import { MegaMenu } from "./MegaMenu";
import { FAQAccordion } from "./faq-accordion";
import { initTestimonialsSwiper } from "./testimonials-swiper";
import { initProductCardBannerSwiper } from "./product-card-banner-swiper";
import { initProductCardFiltersSwiper } from "./product-card-filters-swiper";

import "../css/style.scss";
import "../css/header.scss";
import "../css/timeline.scss";
import "../css/faq.scss"
import {TimeLine} from "./TimeLine.ts";
// Make headroom available globally - handle both default and named exports
const Headroom = (HeadroomModule as any).default || (HeadroomModule as any) || HeadroomModule;
(window as any).Headroom = Headroom;
(window as any).clearAllBodyScrollLocks = clearAllBodyScrollLocks;

const initSwiperThumbs = () => {
  // Initialize keyvisual swiper (if exists)
  const keyvisualElement = document.querySelector(".keyvisual");
  if (keyvisualElement) {
    // Helper function to manage slide accessibility
    const updateSlideAccessibility = (swiper: any) => {
      const slides = swiper.slides;
      slides.forEach((slide: HTMLElement, index: number) => {
        const isActive = index === swiper.activeIndex;
        // Select all naturally focusable elements - use a more comprehensive selector
        // Exclude navigation buttons by checking if they're not inside the slide
        const focusableElements = slide.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');

        // Filter out navigation buttons (they should not be affected)
        const slideContentElements = Array.from(focusableElements).filter((el: Element) => {
          return !el.classList.contains('keyvisual-button-prev') &&
                 !el.classList.contains('keyvisual-button-next') &&
                 !el.classList.contains('swiper-pagination-bullet');
        });

        if (isActive) {
          // Active slide: make content focusable
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
          slideContentElements.forEach((el: HTMLElement) => {
            // Only remove tabindex if it was set to -1 (restore natural focusability)
            if (el.getAttribute('tabindex') === '-1') {
              el.removeAttribute('tabindex');
            }
          });
        } else {
          // Inactive slides: hide from keyboard navigation
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('inert', '');
          slideContentElements.forEach((el: HTMLElement) => {
            el.setAttribute('tabindex', '-1');
          });
        }
      });
    };

    const keyvisual_swiper = new Swiper(".keyvisual", {
      modules: [Pagination, Navigation, A11y],
      spaceBetween: 10,
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
      navigation: {
        nextEl: ".keyvisual-button-next",
        prevEl: ".keyvisual-button-prev",
      },
      a11y: {
        enabled: true,
        prevSlideMessage: (window as any).SwiperTranslations?.prevSlideMessage,
        nextSlideMessage: (window as any).SwiperTranslations?.nextSlideMessage,
        firstSlideMessage: 'This is the first slide',
        lastSlideMessage: 'This is the last slide',
        paginationBulletMessage: (window as any).SwiperTranslations?.paginationBulletMessage,
      },
      on: {
        init: function() {
          // Small delay to ensure DOM is fully rendered
          setTimeout(() => updateSlideAccessibility(this), 100);

          // Prevent scroll when using keyboard on pagination bullets
          const paginationBullets = document.querySelectorAll('.keyvisual .swiper-pagination-bullet');
          paginationBullets.forEach((bullet) => {
            bullet.addEventListener('keydown', (e: Event) => {
              const keyEvent = e as KeyboardEvent;
              if (keyEvent.key === ' ' || keyEvent.key === 'Enter') {
                e.preventDefault();
                (bullet as HTMLElement).click();
              }
            });
          });
        },
        slideChange: function() {
          updateSlideAccessibility(this);
        },
      },
    });
  }

  // Initialize gallery swipers for each gallery container
  const galleryContainers = document.querySelectorAll('[data-gallery-id]');

  galleryContainers.forEach((container) => {
    const galleryId = container.getAttribute('data-gallery-id');
    if (!galleryId) return;

    // Initialize thumbs swiper for this gallery
    const thumbsElement = container.querySelector('.thumbs') as HTMLElement;
    let thumbs_swiper = null;

    if (thumbsElement) {
      thumbs_swiper = new Swiper(thumbsElement, {
        modules: [FreeMode],
        spaceBetween: 10,
        slidesPerView: "auto",
        freeMode: true,
        watchSlidesProgress: true,
      });
    }

    // Initialize main swiper for this gallery
    const mainElement = container.querySelector('.main') as HTMLElement;
    if (mainElement) {
      const main_swiper = new Swiper(mainElement, {
        modules: [Navigation, Thumbs],
        spaceBetween: 10,
        navigation: {
          nextEl: `[data-swiper-next="${galleryId}"]`,
          prevEl: `[data-swiper-prev="${galleryId}"]`,
        },
        thumbs: thumbs_swiper ? {
          swiper: thumbs_swiper,
        } : undefined,
        on: {
          init: function () {
            updateCounter(this, galleryId);
          },
          slideChange: function () {
            updateCounter(this, galleryId);
          },
        },
      });
    }
  });

  function updateCounter(swiper: any, galleryId: string) {
    const counter = document.querySelector(`[data-swiper-counter="${galleryId}"]`) as HTMLElement;
    if (counter) {
      counter.innerHTML = `<span class="text-blue-500 text-[16px]/[1.6] tracking-1p font-medium pr-4">${swiper.realIndex + 1
        }</span><span class="text-grey-300 text-[16px]/[1.6] tracking-1p font-medium border-l border-grey-200 pl-4">${swiper.slides.length
        }</span>`;
    }
  }

  const productCardSwiper = new Swiper(".product-swiper", {
    modules: [Navigation, Pagination],
    spaceBetween: 16,
    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev",
    },
    pagination: {
      el: ".swiper-pagination",
      clickable: true,
    },
    breakpoints: {
      640: {
        slidesPerView: 1,
        navigation: {
          enabled: false,
        },
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
      },
      848: {
        slidesPerView: 2,
        navigation: {
          enabled: true,
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
      },
      1284: {
        slidesPerView: 3,
        navigation: {
          enabled: true,
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
      },
    },
  });
};

const initGallery = () => {
  const galleries = document.querySelectorAll(".js-gallery");
  if (!galleries) return;

  galleries.forEach((g) => {
    const gallery = lightGallery(g as HTMLElement, {
      licenseKey: "a1f2b3c4d5e6f7g8h9i0",
      selector: ".js-gallery-image",
      download: false,
      zoom: false,
      share: false,
      backdropDuration: 100,
      mobileSettings: {
        controls: true,
      },
    });
  });
};

const initVideoGallery = () => {
  const videoGalleries = document.querySelectorAll(".js-video-gallery");
  if (!videoGalleries) return;

  videoGalleries.forEach((g) => {
    const gallery = lightGallery(g as HTMLElement, {
      licenseKey: "a1f2b3c4d5e6f7g8h9i0",
      plugins: [lgVideo],
      selector: ".js-video-item",
      download: false,
      zoom: false,
      share: false,
      backdropDuration: 100,
      mobileSettings: {
        controls: true,
      },
    });
  });
};
const initGoogleMaps = () => {
  if (typeof window.google === "undefined") {
    return;
  }

  const mapElement = document.getElementById("map");
  if (!mapElement) {
    return;
  }

  const lat = mapElement.dataset.lat;
  const lon = mapElement.dataset.lon;

  const location = { lat: parseFloat(lat), lng: parseFloat(lon) };

  const map = new window.google.maps.Map(mapElement, {
    center: location,
    zoom: 15,
    mapId: "b3c99845942ee69",
  });

  if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
    new window.google.maps.marker.AdvancedMarkerElement({
      map,
      position: location,
      title: "Google HQ",
    });
  } else {
    new window.google.maps.Marker({
      position: location,
      map: map,
    });
  }
};

const loadGoogleMaps = () => {
  if (document.getElementById("google-maps-script")) {
    return;
  }

  const script = document.createElement("script");
  script.id = "google-maps-script";
  script.src =
    "https://maps.googleapis.com/maps/api/js?key=AIzaSyCByh80Ktxlu8vSrrKMrMBm0JCA5nxdpuk&libraries=core,marker";
  script.async = true;
  script.defer = true;
  script.onload = () => {
    initGoogleMaps();
  };

  document.body.appendChild(script);
};

const initContactsFilter = () => {
  document.querySelectorAll(".contact-list")?.forEach((x) => {
    x.querySelector("select")?.addEventListener("change", function () {
      x.querySelectorAll(`.contact-group`)?.forEach((group) => {
        group.classList.toggle(
          "hidden",
          this.value != "all" && group.id != this.value
        );
      });
    });
  });
};

const initStickyHeader = () => {
  const root = document.documentElement;

  const onScroll = () => {
    window.requestAnimationFrame(() => {
      if (window.scrollY <= 150) {
        root.classList.add("headroom--top");
        root.classList.remove("headroom--not-top");
      } else {
        root.classList.add("headroom--not-top");
        root.classList.remove("headroom--top");
      }
    });
  };

  // Initial state
  onScroll();

  // Listen for further scrolls
  window.addEventListener("scroll", onScroll);
};

const initMegaMenu = () => {
  console.log('Attempting initialization', new Date());

  // Initialize the MegaMenu component
  const megaMenuInstance = new MegaMenu();
  megaMenuInstance.init();

  // Make MegaMenu available globally for debugging if needed
  (window as any).megaMenuInstance = megaMenuInstance;
};

const initTimeline = () => {
    const timeline = new TimeLine();
}

const initFAQAccordion = () => {
  new FAQAccordion(false);
}

const initMultiTabBanner = () => {
  document.querySelectorAll('.multi-tab-content')?.forEach(multiTabContent => {
    const buttons = multiTabContent.querySelectorAll('.multi-tab-content-button');
    const tabs = multiTabContent.querySelectorAll('.multi-tab-content-tab');

    buttons.forEach(btn => {
      btn.addEventListener('change', function() {
        tabs.forEach(c => {
          const tabElement = c as HTMLElement;
          const btnElement = btn as HTMLElement;
          tabElement.classList.toggle('hidden', tabElement.dataset.tab !== btnElement.dataset.tab);
        });
      });
    });
  })
}

const initCardFilters = () => {
  document.querySelectorAll(".custom-select-wrapper").forEach((wrapper) => {
    const select = wrapper.querySelector("select");
    const customSelect = wrapper.querySelector(".custom-select");
    const trigger = wrapper.querySelector(".custom-select-trigger");
    const optionsContainer = wrapper.querySelector(".custom-options");
    const options = Array.from(optionsContainer.querySelectorAll(".custom-option"));
    const triggerText = wrapper.querySelector(".trigger-text");
    let currentIndex = -1;

    const closeAll = (except?: Element) => {
      document.querySelectorAll(".custom-select-wrapper .custom-options").forEach((container) => {
        if (container !== except) {
          container.classList.add("hidden");
          const parent = container.closest(".custom-select");
          if (parent) parent.setAttribute("aria-expanded", "false");
        }
      });
    };
    const toggle = (force?: boolean) => {
      const isOpen = force !== undefined ? force : !optionsContainer.classList.contains("hidden");
      if (!isOpen) closeAll(optionsContainer);
      optionsContainer.classList.toggle("hidden", isOpen);
      customSelect?.setAttribute("aria-expanded", String(!isOpen));
      if (!isOpen) {
        currentIndex = -1;
        options.forEach(o => { o.classList.remove("bg-blue-0", "text-blue-500"); o.classList.remove("bg-blue-100"); });
        customSelect?.removeAttribute("aria-activedescendant");
      } else {
        customSelect?.focus();
        const selectedValue = select.value;
        const idx = options.findIndex(o => o.getAttribute("data-value") === selectedValue);
        if (idx >= 0) {
          currentIndex = idx;
          options[idx].classList.add("bg-blue-100", "text-blue-500");
          customSelect?.setAttribute("aria-activedescendant", options[idx].id || "");
        }
      }
    };

    const selectOption = (option: Element) => {
      const value = option.getAttribute("data-value");
      select.value = value;
      triggerText.textContent = option.textContent;
      options.forEach(o => o.removeAttribute("aria-selected"));
      option.setAttribute("aria-selected", "true");
      toggle(true);
      select.dispatchEvent(new Event("change"));
    };

    trigger.addEventListener("click", () => {
      toggle();
    });

    customSelect?.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (optionsContainer.classList.contains("hidden")) {
          toggle();
        } else if (currentIndex >= 0 && options[currentIndex]) {
          selectOption(options[currentIndex]);
        }
        return;
      }
      if (e.key === "Escape") {
        toggle(true);
        customSelect.focus();
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const isOpen = !optionsContainer.classList.contains("hidden");
        if (!isOpen) {
          toggle();
        }
        const dir = e.key === "ArrowDown" ? 1 : -1;
        const newIndex = Math.min(Math.max(currentIndex + dir, 0), options.length - 1);
        if (newIndex !== currentIndex) {
          options.forEach(o => { o.classList.remove("bg-blue-0", "text-blue-500"); o.classList.remove("bg-blue-100"); });
          currentIndex = newIndex;
          const opt = options[currentIndex];
          opt.classList.add("bg-blue-100", "text-blue-500");
          customSelect.setAttribute("aria-activedescendant", opt.id || "");
        }
        return;
      }
    });

    options.forEach((option, i) => {
      if (!option.id) option.id = "opt-" + i;
      option.addEventListener("click", () => {
        selectOption(option);
      });
    });

    document.addEventListener("click", (e) => {
      if (!wrapper.contains(e.target as Node)) {
        toggle(true);
      }
    });
  });

  document.querySelectorAll('.filtrable-list').forEach(list => {
    const cards = list.querySelectorAll('.filtrable-card');
    const filters = list.querySelectorAll('select');

    filters.forEach(filter => {
      filter.addEventListener('change', function () {
        const categories = list.querySelectorAll('.filtrable-category');

        cards.forEach(card => {
          var hideCard = false;
          const cardFilters = JSON.parse(card.dataset["filters"])

          filters.forEach(f => {
            const hide = f.value != 'all' && !cardFilters.includes(Number(f.value));
            console.log(cardFilters, f.value, hide)
            if (hide) hideCard = true;
          });
          card.classList.toggle('hidden', hideCard)
        });

        var showNoCardsText = true;
        categories.forEach(category => {
          const categoryCards = [...category.querySelectorAll('.filtrable-card')];
          const shownCards = categoryCards.filter(e => !e.classList.contains('hidden'));
          const hideCategory = shownCards.length === 0
          category.classList.toggle('hidden', hideCategory)
          if (!hideCategory) showNoCardsText = false;
        });

        list.querySelector('.filtrable-category-empty')?.classList.toggle('hidden', !showNoCardsText);

        const statusEl = list.querySelector<HTMLElement>('.filter-status');
        if (statusEl) {
          if (showNoCardsText) {
            statusEl.textContent = statusEl.dataset.noResultsText || '';
          } else {
            const visible = document.querySelectorAll('.filtrable-card:not(.hidden)').length;
            statusEl.textContent = (statusEl.dataset.filteredText || '') + ': ' + visible;
          }
        }
      })
    })
  });
}

const initSidebarCategories = () => {
    document.querySelectorAll<HTMLElement>('.sidebar-categorie').forEach((element) => {
        element.addEventListener('click', (event) => {
            document.querySelectorAll<HTMLElement>('.sidebar-categorie').forEach((el) => {
                el.classList.remove('active');
            });

            document.querySelectorAll<HTMLElement>('.sidebar-content').forEach((el) => {
                el.style.display = 'none';
            });

            const selectedCategorie = event.currentTarget as HTMLElement | null;
            const contentToShow = document.querySelector<HTMLElement>(
                `#categorie-content-${selectedCategorie?.id}`
            );

            if (contentToShow) {
                contentToShow.style.display = 'block';
            }

            selectedCategorie?.classList.add('active');
        });
    });

    // Inicializacija prve kategorije
    const firstCategorie = document.querySelector<HTMLElement>('.sidebar-categorie');
    const firstCategorieContent = document.querySelector<HTMLElement>('.sidebar-content');

    if (firstCategorie) {
        firstCategorie.classList.add('active');
    }
    if (firstCategorieContent) {
        firstCategorieContent.style.display = 'block';
    }
};


const initStepByStepBanner = () => {
  const lists = document.querySelectorAll('.timeline-list');
  if (!lists || lists.length === 0) return;

  const recalc = () => {
    lists.forEach(list => {
      // Skip calculation if the list is not visible (width/height is 0)
      const listRect = (list as HTMLElement).getBoundingClientRect();
      if (listRect.width === 0 || listRect.height === 0) {
        return;
      }

      list.querySelectorAll('.timeline-item').forEach(item => {
        const article = item.querySelector('.timeline-article') as HTMLElement | null;
        const lineSvg = item.querySelector('.timeline-line, .timeline-svg') as SVGElement | null;
        const circleCandidates = item.querySelectorAll('.timeline-svg circle, .timeline-circle circle') as NodeListOf<SVGCircleElement>;
        const title = item.querySelector('.timeline-step-title') as HTMLElement | null;

        // Reset offset before measuring to avoid compounding previous adjustments
        if (title) title.style.removeProperty('--timeline-title-offset');

        // Vertically center stepTitle with the middle of the most prominent circle (largest height)
        if (circleCandidates.length && title) {
          let circleRect: DOMRect | null = null;
          circleCandidates.forEach(c => {
            const r = c.getBoundingClientRect();
            if (!circleRect || r.height > circleRect.height) circleRect = r;
          });
          if (circleRect && circleRect.height > 0) {
            const titleRect = title.getBoundingClientRect();
            const offset = (circleRect.top + circleRect.height / 2) - (titleRect.top + titleRect.height / 2);
            title.style.setProperty('--timeline-title-offset', `${offset}px`);
          }
        }

        // Stretch the vertical line to the visual bottom of the content, accounting for margins and title offset
        if (article && lineSvg) {
          const svgTop = (lineSvg as Element).getBoundingClientRect().top;
          const articleBottom = article.getBoundingClientRect().bottom;
          const titleBottom = title ? title.getBoundingClientRect().bottom : 0;
          const itemBottom = (item as HTMLElement).getBoundingClientRect().bottom; // include <li> margins
          const contentBottom = Math.max(articleBottom, titleBottom, itemBottom);
          const height = Math.max(0, Math.round(contentBottom - svgTop));
          lineSvg.setAttribute('height', String(height));
          const line = lineSvg.querySelector('line');
          if (line) line.setAttribute('y2', String(height));
        }
      })
    })
  };

  // Create IntersectionObserver to detect when timeline lists become visible
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Use requestAnimationFrame to ensure DOM is ready for measurement
        requestAnimationFrame(() => {
          setTimeout(recalc, 10); // Small delay to ensure layout is complete
        });
      }
    });
  }, { threshold: 0.1 });

  // Observe all timeline lists
  lists.forEach(list => {
    observer.observe(list as HTMLElement);
  });

  // Recalculate on load and on resize (debounced)
  recalc();
  window.addEventListener('load', recalc);

  let resizeTimeout: number | null = null;
  window.addEventListener('resize', () => {
    if (resizeTimeout) window.clearTimeout(resizeTimeout as number);
    resizeTimeout = window.setTimeout(recalc, 150);
  });
};

const initFormSliders = () => {
  document.querySelectorAll(".range-custom-input").forEach((wrapper) => {
    const input = wrapper.querySelector(
      'input[type="range"]',
    ) as HTMLInputElement;
    const valueDisplay = wrapper.querySelector(".range-value") as HTMLElement;

    function update() {
      const min = Number(input.min || "0");
      const max = Number(input.max || "100");
      const val = Number(input.value);
      const percent = ((val - min) / (max - min)) * 100;

      input.style.setProperty("--progress", `${percent}%`);
      valueDisplay.textContent = val.toString();
    }

    update();
    input.addEventListener("input", update);
  });
}

const initReviewCards = () => {
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const btn = target.closest('.read-more-btn') as HTMLElement | null;
    if (!btn) return;

    const card = btn.closest('.review-card') as HTMLElement | null;
    if (!card) return;

    const text = card.querySelector('.review-text') as HTMLElement | null;
    if (!text) return;

    const readMoreText = btn.dataset.readMore;
    const hideText = btn.dataset.hide;

    const isClamped = text.classList.toggle('line-clamp-5');

    if (readMoreText && hideText) {
      btn.textContent = isClamped ? readMoreText : hideText;
    }

    if ((window as any).swiper?.updateAutoHeight) {
        (window as any).swiper.updateAutoHeight();
    }
  });
};

const adjustBlockGridColumnSpan = () => {
  const items = document.querySelectorAll('.items-div-custom-column-span');
  const isMobile = window.innerWidth < 1024;

  items.forEach(item => {
    const columnSpan = (item as HTMLElement).getAttribute('data-column-span');
    if (isMobile && columnSpan === '4') {
      (item as HTMLElement).style.gridColumn = 'span 8';
    } else if (columnSpan) {
      (item as HTMLElement).style.gridColumn = `span ${columnSpan}`;
    }
  });
};

function initSaleReceivablesTables(root: ParentNode = document): void {
  const components = root.querySelectorAll<HTMLElement>('[data-component="sale-receivables-table"]');

  components.forEach((component) => {
    if (component.dataset.initialized === "true") return;
    component.dataset.initialized = "true";

    const sortButtons = Array.from(component.querySelectorAll<HTMLButtonElement>('.receivablesSortButton'));
    const tbody = component.querySelector<HTMLTableSectionElement>('tbody.receivablesTbody');

    if (!sortButtons.length || !tbody) return;

    // Save the original order (from Umbraco)
    const originalRows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr'));
    let currentMode = "";

    const restoreOriginalOrder = () => {
      originalRows.forEach(r => tbody.appendChild(r));
    };

    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    const updateActiveButtons = (mode: string) => {
      sortButtons.forEach((button) => {
        const neutralIcon = button.querySelector<SVGElement>('.receivablesSortIconNeutral');
        const ascIcon = button.querySelector<SVGElement>('.receivablesSortIconAsc');
        const descIcon = button.querySelector<SVGElement>('.receivablesSortIconDesc');
        const key = button.dataset.sortKey ?? '';
        const [activeKey, direction = ''] = mode.split('_');
        const isActive = key === activeKey && Boolean(direction);

        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        button.dataset.sortDirection = isActive ? direction : '';

        neutralIcon?.classList.toggle('hidden', isActive);
        ascIcon?.classList.toggle('hidden', !(direction === 'asc' && key === activeKey));
        descIcon?.classList.toggle('hidden', !(direction === 'desc' && key === activeKey));
      });
    };

    const sortRows = (mode: string) => {
      if (!mode) {
        restoreOriginalOrder();
        return;
      }

      const rows = Array.from(tbody.querySelectorAll<HTMLTableRowElement>('tr'));

      rows.sort((a, b) => {
        const companyA = (a.dataset.company ?? '').trim();
        const companyB = (b.dataset.company ?? '').trim();

        const exposureA = Number.parseFloat(a.dataset.exposure ?? '0') || 0;
        const exposureB = Number.parseFloat(b.dataset.exposure ?? '0') || 0;

        const shareA = Number.parseFloat(a.dataset.share ?? '0') || 0;
        const shareB = Number.parseFloat(b.dataset.share ?? '0') || 0;

        switch (mode) {
          case 'exposure_asc':  return exposureA - exposureB;
          case 'exposure_desc': return exposureB - exposureA;

          case 'share_asc':     return shareA - shareB;
          case 'share_desc':    return shareB - shareA;

          case 'company_desc':  return collator.compare(companyB, companyA);
          case 'company_asc':
          default:              return collator.compare(companyA, companyB);
        }
      });

      rows.forEach(r => tbody.appendChild(r));
    };

    sortButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const key = button.dataset.sortKey ?? '';
        const [activeKey, direction = ''] = currentMode.split('_');

        if (key !== activeKey) {
          currentMode = `${key}_asc`;
        } else if (direction === 'asc') {
          currentMode = `${key}_desc`;
        } else {
          currentMode = '';
        }

        sortRows(currentMode);
        updateActiveButtons(currentMode);
      });
    });

    updateActiveButtons(currentMode);
  });
}

// GTM script in TypeScript
export {};

declare global {
  interface Window {
    dataLayer: {
      event: string;
      click_text?: string;
      page_path?: string;
      [key: string]: any;
    }[];
  }
}

// CLICK tracking (buttons, links, etc.)
document.addEventListener("click", (e: MouseEvent) => {
  if (e.button !== 0) return; // only left click
  if (!(e.target instanceof Element)) return;

  // 1️⃣ First: check data-gtm-event (existing logic)
  const gtmEl = e.target.closest<HTMLElement>("[data-gtm-event]");
  if (gtmEl) {
    const { gtmEvent, gtmClickText, gtmPagePath, gtmFullName } = gtmEl.dataset;
    if (!gtmEvent) return;

    pushToDataLayer(gtmEvent, gtmClickText, gtmPagePath, gtmFullName);
    return;
  }

  // 2️⃣ NEW: check for #submit_button
  const submitButton = e.target.closest<HTMLElement>("#submit_button");
  if (submitButton) {
    pushToDataLayer(
      "NewsletterSignup",
      submitButton.textContent ?? "",
      window.location.pathname
    );
  }
});

// SUBMIT tracking (covers Enter key)
document.addEventListener("submit", (e: Event) => {
  const form = e.target as HTMLFormElement | null;
  if (!form) return;

  // Find submit button inside the form with GTM attribute
  const el = form.querySelector<HTMLElement>("[data-gtm-event]");
  if (!el) return;

  const { gtmEvent, gtmClickText, gtmPagePath, gtmFullName } = el.dataset;
  if (!gtmEvent) return;

  pushToDataLayer(gtmEvent, gtmClickText, gtmPagePath, gtmFullName);
});

// Shared function
function pushToDataLayer(
  event: string,
  clickText?: string,
  pagePath?: string,
  gtmFullName?: string,
) {

  if (!Array.isArray(window.dataLayer)) return;

  const payload: any = {
    event: event.trim(),
    click_text: clickText ?? "",
    page_path: pagePath ?? window.location.pathname
  };

  // ✅ Only add full_name if it exists and is not empty
  if (gtmFullName && gtmFullName.trim() !== "") {
    payload.full_name = gtmFullName.trim();
  }

  window.dataLayer.push(payload);
}
// END END END - GTM script in TypeScript

const initCookieAuditTable = () => {
  document.querySelectorAll(".cky-cookie-audit-table tbody tr").forEach(row => {
    const td = row.querySelector("td");

    if (td) {
      const th = document.createElement("th");
      th.scope = "row";
      th.innerHTML = td.innerHTML;

      td.replaceWith(th);
    }
  });
};

document.addEventListener("DOMContentLoaded", () => {
  // initStickyHeader(); // Disabled - using headroom.js instead
  initMultiTabBanner();
  initGallery();
  initVideoGallery();
  initSwiperThumbs();
  loadGoogleMaps();
  initCardFilters();
  initContactsFilter();
  initSidebarCategories();
  initStepByStepBanner();
  initFormSliders();
  initFAQAccordion();
  initTestimonialsSwiper();
  initProductCardBannerSwiper();
  initProductCardFiltersSwiper();
  initReviewCards();
  initSaleReceivablesTables();
  adjustBlockGridColumnSpan();
  window.addEventListener('resize', adjustBlockGridColumnSpan);

  initCookieAuditTable();

  // Initialize MegaMenu with a slight delay to ensure DOM is fully ready
  setTimeout(() => {
    initMegaMenu();
    initTimeline();
  }, 100);
});
